// src/pages/Veiculos.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getFirestore, doc, getDoc, collection, getDocs, addDoc, updateDoc, deleteDoc
} from 'firebase/firestore';
import { FaPlus, FaEdit, FaTrashAlt, FaFilePdf, FaArrowLeft } from 'react-icons/fa';
import Swal from 'sweetalert2';

// PDF
import jsPDF from 'jspdf';
// --- CORREÇÃO: Importar 'autoTable' como uma função ---
import autoTable from 'jspdf-autotable';

import ModalNovoVeiculo from '../components/ModalNovoVeiculo';
import { useEmpresaId } from "../context/EmpresaContext";

const db = getFirestore();

const Veiculos = () => {
  const { clienteId } = useParams();
  const empresaId = useEmpresaId();
  const navigate = useNavigate();

  const [cliente, setCliente] = useState(null);
  const [veiculos, setVeiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [veiculoSelecionado, setVeiculoSelecionado] = useState(null);
  const [editando, setEditando] = useState(false);

  // Helpers de caminho no modelo novo
  const refCliente = (empId, cliId) => doc(db, 'empresas', empId, 'clientes', cliId);
  const colVeiculos = (empId, cliId) => collection(db, 'empresas', empId, 'clientes', cliId, 'veiculos');
  const refVeiculo = (empId, cliId, veicId) => doc(db, 'empresas', empId, 'clientes', cliId, 'veiculos', veicId);

  const fetchVeiculosECliente = useCallback(async () => {
    if (!empresaId || !clienteId) return;
    setLoading(true);
    try {
      // Cliente
      const clienteSnap = await getDoc(refCliente(empresaId, clienteId));
      if (!clienteSnap.exists()) {
        Swal.fire('Erro', 'Cliente não encontrado.', 'error');
        navigate('/clientes');
        setLoading(false);
        return;
      }
      setCliente({ id: clienteSnap.id, ...clienteSnap.data() });

      // Veículos
      const veiculosSnap = await getDocs(colVeiculos(empresaId, clienteId));
      const lista = veiculosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      lista.sort((a, b) => {
        const byMarca = (a.marca || '').localeCompare(b.marca || '');
        if (byMarca !== 0) return byMarca;
        return (a.modelo || '').localeCompare(b.modelo || '');
      });
      setVeiculos(lista);
    } catch (error) {
      console.error("Erro detalhado ao buscar dados:", error);
      Swal.fire('Erro', `Não foi possível carregar os dados. Detalhe: ${error.message}`, 'error');
    }
    setLoading(false);
  }, [empresaId, clienteId, navigate]);

  useEffect(() => {
    fetchVeiculosECliente();
  }, [fetchVeiculosECliente]);

  const handleNovoVeiculo = () => {
    setVeiculoSelecionado(null);
    setEditando(false);
    setIsModalOpen(true);
  };

  const handleEditarVeiculo = (veiculo) => {
    setVeiculoSelecionado(veiculo);
    setEditando(true);
    setIsModalOpen(true);
  };

  const handleSalvarVeiculo = async (formData) => {
    if (!empresaId) return;
    try {
      const veiculoPayload = { ...formData, clienteId };

      if (editando && veiculoSelecionado?.id) {
        await updateDoc(refVeiculo(empresaId, clienteId, veiculoSelecionado.id), veiculoPayload);
        Swal.fire('Sucesso!', 'Veículo atualizado com sucesso.', 'success');
      } else {
        await addDoc(colVeiculos(empresaId, clienteId), veiculoPayload);
        Swal.fire('Sucesso!', 'Veículo adicionado com sucesso.', 'success');
      }

      setIsModalOpen(false);
      fetchVeiculosECliente();
    } catch (error) {
      console.error("Erro ao salvar veículo:", error);
      Swal.fire('Erro', 'Não foi possível salvar o veículo.', 'error');
    }
  };

  const handleExcluirVeiculo = async (id) => {
    if (!empresaId) return;
    const result = await Swal.fire({
      title: 'Deseja excluir este veículo?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Sim, excluir'
    });
    if (result.isConfirmed) {
      try {
        await deleteDoc(refVeiculo(empresaId, clienteId, id));
        Swal.fire('Excluído!', 'O veículo foi removido.', 'success');
        fetchVeiculosECliente();
      } catch (error) {
        console.error("Erro ao excluir veículo:", error);
        Swal.fire('Erro', 'Não foi possível excluir o veículo.', 'error');
      }
    }
  };

  // Exportar PDF
  const exportarPDF = () => {
    const docPDF = new jsPDF();
    docPDF.text(`Veículos de ${cliente?.nome || 'Cliente'}`, 14, 20);

    const tableColumn = ["Placa", "Tipo", "Marca", "Modelo", "Ano", "Cor"];
    const tableRows = veiculos.map(v => [
      v.placa || '',
      v.tipo || '',
      v.marca || '',
      v.modelo || '',
      v.ano || '',
      v.cor || ''
    ]);

    // --- CORREÇÃO: Chamar 'autoTable' como uma função, passando o 'docPDF' ---
    autoTable(docPDF, {
      head: [tableColumn],
      body: tableRows,
      startY: 30,
    });

    const nomeArquivo = cliente?.nome ? cliente.nome.replace(/\s/g, '_') : clienteId;
    docPDF.save(`veiculos_${nomeArquivo}.pdf`);
  };

  return (
    <div className="p-6 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <button
            onClick={() => navigate('/clientes')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 mb-2"
          >
            <FaArrowLeft /> Voltar para Clientes
          </button>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
            Veículos de <span className="text-blue-600">{cliente?.nome || '...'}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportarPDF}
            className="bg-red-500 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-600"
          >
            <FaFilePdf /> PDF
          </button>
          <button
            onClick={handleNovoVeiculo}
            className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            <FaPlus /> Novo Veículo
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="p-4">Placa</th>
              <th className="p-4">Tipo</th>
              <th className="p-4">Marca</th>
              <th className="p-4">Modelo</th>
              <th className="p-4">Ano</th>
              <th className="p-4">Cor</th>
              <th className="p-4 text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="text-center p-10">A carregar veículos...</td></tr>
            ) : veiculos.length === 0 ? (
              <tr><td colSpan="7" className="text-center p-10">Nenhum veículo cadastrado.</td></tr>
            ) : (
              veiculos.map(v => (
                <tr key={v.id} className="border-b dark:border-gray-700 last:border-0">
                  <td className="p-4 font-mono font-bold">{v.placa}</td>
                  <td className="p-4">{v.tipo}</td>
                  <td className="p-4">{v.marca}</td>
                  <td className="p-4">{v.modelo}</td>
                  <td className="p-4">{v.ano}</td>
                  <td className="p-4">{v.cor}</td>
                  <td className="p-4 flex justify-center gap-2">
                    <button
                      onClick={() => handleEditarVeiculo(v)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-full"
                      title="Editar"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleExcluirVeiculo(v.id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-full"
                      title="Excluir"
                    >
                      <FaTrashAlt />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ModalNovoVeiculo
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSalvarVeiculo}
        veiculo={veiculoSelecionado}
        editando={editando}
      />
    </div>
  );
};

export default Veiculos;
